/**
 * @fileoverview Session Overlay Manager
 * 
 * Manages and displays different overlay states based on session transitions
 * in the proctor sync document. Monitors session state changes and shows
 * appropriate overlays for transfer in/out, reconnection, and other states.
 * 
 * @module vtc/components/Overlays
 */

import { useEffect, useState } from 'react';
import useSessionStore from '../../stores/session';

const SHOW_TRANSFER_OVERLAY_DURATION = 5000;

//import TransferIn from './TransferIn';
//import TransferOut from './TransferOut';

/**
 * Overlay state types
 * @typedef {'none'|'transfer-in'|'transfer-out'|'reconnecting'|'ending'} OverlayState
 */

/**
 * SessionOverlayManager Component
 * 
 * Monitors session changes in the proctor sync document and displays
 * appropriate overlays based on session state transitions.
 * 
 * **Overlay States:**
 * - **transfer-in**: Session is being transferred to this proctor
 * - **transfer-out**: Session is being transferred to another proctor
 * - **reconnecting**: Session is reconnecting after disconnect
 * - **ending**: Session is being ended
 * - **none**: No overlay shown
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.sessionSid - Session identifier to monitor
 * @returns {JSX.Element|null}
 * 
 * @example
 * <SessionOverlayManager sessionSid="session_123" />
 */
const SessionOverlayManager = ({ sessionSid }) => {
    const [overlayState, setOverlayState] = useState('none');
    const [overlayData, setOverlayData] = useState(null);

    // Get session data from store

    const session = useSessionStore((state) => state.sessions[sessionSid]);

    // Monitor session changes from sync document
    useEffect(() => {
        if (!sessionSid) return;
        // ok now based on the session data, we need to decide which overlay to show...
        handleSessionChanges(session);
    }, [session]);

    /**
     * Handles session state changes from the proctor sync document
     * 
     * @param {Object} changes - Session change data
     * @param {string} [changes.transferStatus] - Transfer status ('transferring-out', 'transferring-in')
     * @param {string} [changes.transferToRoomSid] - Target room for transfer
     * @param {string} [changes.transferFromRoomSid] - Source room for transfer
     * @param {string} [changes.transferNote] - Transfer note/reason
     * @param {string} [changes.location] - Session location state
     * @param {boolean} [changes.isReconnecting] - Whether session is reconnecting
     * @param {boolean} [changes.isEnding] - Whether session is ending
     */
    const handleSessionChanges = (session) => {

        /*
            It is possible to have some conditions still present when new ones become present, so we need 
            to work backwards...
            1. Is the session ending?
            3. Is the session transferring?
        */


        if (session?.sessionData?.ending) { // Is there an ending object...

        } else if (session?.sessionData?.transfer) { //is there a transfer object
            const durationSinceTransfer = Date.now() - session.sessionData.transfer.transferDate;
            if (durationSinceTransfer < SHOW_TRANSFER_OVERLAY_DURATION) {
                setOverlayState('transfer-in');
                setOverlayData(session.sessionData.transfer);
            }
        }
    };

    // Render appropriate overlay based on state
    switch (overlayState) {
        case 'transfer-in':
            return null;
            return (
                <TransferIn
                    sessionSid={sessionSid}
                    sessionData={session}
                    overlayData={overlayData}
                    onComplete={() => setOverlayState('none')}
                />
            );

        case 'transfer-out':
            return null;
            return (
                <TransferOut
                    sessionSid={sessionSid}
                    sessionData={session}
                    overlayData={overlayData}
                    onComplete={() => setOverlayState('none')}
                />
            );

        case 'reconnecting':
            return (
                <div className="session-overlay reconnecting">
                    <div className="overlay-content">
                        <div className="spinner" />
                        <p>{overlayData?.message || 'Reconnecting...'}</p>
                    </div>
                </div>
            );

        case 'ending':
            return null;
            return (
                <div className="session-overlay ending">
                    <div className="overlay-content">
                        <div className="spinner" />
                        <p>{overlayData?.message || 'Ending session...'}</p>
                    </div>
                </div>
            );

        case 'none':
        default:
            return null;
    }
};

export default SessionOverlayManager;
